import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrgDraft, OrgDraftStep } from '@newgenus/common';
import { OrgDraftService } from '../services/org-draft.service';

/**
 * Drafts-picker dialog (plan §7 — `dialogs/drafts-picker-dialog/`).
 *
 * Surfaced when the user is at the D11 cap (3 in-progress drafts) and tries
 * to start a 4th. Lists the existing drafts with two actions per row:
 *  - **Open** — navigate to `/organization/new/:draftKey` to resume.
 *  - **Trash** — hard-delete the draft to free a slot, then re-attempt the
 *    new-draft flow.
 *
 * The dialog returns one of:
 *  - `{ action: 'open', draftKey }`  — caller should navigate.
 *  - `{ action: 'trashed' }`         — caller should retry `startDraft()`.
 *  - `{ action: 'cancel' }` / `undefined` — caller does nothing.
 */
export interface DraftsPickerData {
    drafts: OrgDraft[];
}

export type DraftsPickerResult = { action: 'open'; draftKey: string } | { action: 'trashed' } | { action: 'cancel' };

@Component({
    selector: 'newgenus-drafts-picker-dialog',
    templateUrl: './drafts-picker-dialog.component.html',
    styleUrls: ['./drafts-picker-dialog.component.scss'],
    standalone: false,
})
export class DraftsPickerDialogComponent {
    public drafts: OrgDraft[];
    public busyKey: string | null = null;

    constructor(
        public dialogRef: MatDialogRef<DraftsPickerDialogComponent, DraftsPickerResult>,
        @Inject(MAT_DIALOG_DATA) data: DraftsPickerData,
        private draftService: OrgDraftService
    ) {
        this.drafts = [...data.drafts].sort((a, b) => this.modifiedMs(b) - this.modifiedMs(a));
    }

    public displayName(draft: OrgDraft): string {
        const name = draft.basics?.name?.trim();
        return name || 'Untitled draft';
    }

    public stepLabel(step: OrgDraftStep): string {
        return (
            {
                1: 'Source',
                2: 'Basics',
                3: 'Structure',
                4: 'Staff',
                5: 'Plan & Features',
                6: 'Review',
            }[step] ?? `Step ${step}`
        );
    }

    public lastModified(draft: OrgDraft): Date | null {
        const ms = this.modifiedMs(draft);
        return ms ? new Date(ms) : null;
    }

    public expiresIn(draft: OrgDraft): number {
        const expiresMs = (draft.expiresOn as { toMillis?: () => number })?.toMillis?.() ?? 0;
        if (!expiresMs) return Number.POSITIVE_INFINITY;
        return Math.max(0, Math.ceil((expiresMs - Date.now()) / (1000 * 60 * 60 * 24)));
    }

    public hasExpiry(draft: OrgDraft): boolean {
        const expiresMs = (draft.expiresOn as { toMillis?: () => number })?.toMillis?.() ?? 0;
        return expiresMs > 0;
    }

    public open(draft: OrgDraft): void {
        this.dialogRef.close({ action: 'open', draftKey: draft.key });
    }

    public async trash(draft: OrgDraft): Promise<void> {
        if (this.busyKey) return;
        const ok = window.confirm(
            `Delete draft "${this.displayName(draft)}"?\n\nThis frees a slot so you can start a new organization. The draft cannot be recovered.`
        );
        if (!ok) return;
        this.busyKey = draft.key;
        try {
            await this.draftService.hardDelete(draft.key);
            this.drafts = this.drafts.filter((d) => d.key !== draft.key);
            // If they trashed at least one and now the list is below cap, close
            // with `trashed` so the wizard retries `startDraft()`. Otherwise
            // stay open so they can trash more or open one.
            this.dialogRef.close({ action: 'trashed' });
        } catch (err) {
            console.error('DraftsPickerDialog hardDelete failed:', err);
            window.alert('Failed to delete the draft. Please try again.');
        } finally {
            this.busyKey = null;
        }
    }

    public cancel(): void {
        this.dialogRef.close({ action: 'cancel' });
    }

    private modifiedMs(draft: OrgDraft): number {
        const ts = draft.modifiedOn ?? draft.createdOn;
        const ms = (ts as { toMillis?: () => number })?.toMillis?.();
        return typeof ms === 'number' ? ms : 0;
    }
}
